import { api, request } from '../client'
import type { AuthSession, MfaConfirmResult, MfaSetup, MfaStatus } from '../types'

/**
 * Verifica in due passaggi — OPS-04.
 *
 * Il 2FA sta **sull'account** e non sull'appartenenza: protegge chi sei, non il
 * ruolo che hai in una palestra. Chi lavora per due organizzazioni non configura
 * due volte l'autenticatore, ed è il motivo per cui queste rotte stanno sotto
 * `/me` e non sotto `/organization`.
 */

/**
 * `POST /auth/mfa/verify` — **pubblica**.
 *
 * Chi la chiama non ha ancora una sessione: ha solo il buono emesso dal login,
 * e quel buono *è* la credenziale. Per questo `auth: false` — allegare un bearer
 * che non esiste ancora non avrebbe senso — e `organization: false`, perché
 * l'organizzazione la si sceglie dopo essere entrati.
 *
 * Uno fra `code` e `recoveryCode`, non entrambi: il codice di recupero è la via
 * d'uscita quando il telefono non c'è più.
 */
export function verifyMfa(body: {
  mfaToken: string
  code?: string
  recoveryCode?: string
}): Promise<AuthSession> {
  return request<AuthSession>('/auth/mfa/verify', {
    method: 'POST',
    body,
    auth: false,
    organization: false,
  })
}

/** `GET /me/mfa`: attivo, in sospeso, e quanti codici di recupero restano. */
export function getMfaStatus(signal?: AbortSignal): Promise<MfaStatus> {
  return api.get<MfaStatus>('/me/mfa', {
    organization: false,
    ...(signal ? { signal } : {}),
  })
}

/**
 * `POST /me/mfa/setup`: genera un segreto **non ancora attivo**.
 *
 * Fra «ho generato il segreto» e «l'ho inquadrato e funziona» c'è un intervallo
 * in cui il 2FA non deve essere attivo: senza, chi sbaglia a configurare l'app
 * resterebbe chiuso fuori dal proprio account senza rimedio. Ripetere la
 * chiamata mentre una configurazione è in sospeso genera un segreto nuovo e
 * butta il precedente — è ciò che serve a chi ha chiuso la schermata del codice
 * prima di salvarlo. Su un 2FA già attivo invece è `409 mfa_already_enabled`.
 */
export function setupMfa(): Promise<MfaSetup> {
  return api.post<MfaSetup>('/me/mfa/setup', undefined, { organization: false })
}

/**
 * `POST /me/mfa/confirm`: attiva il 2FA e restituisce i codici di recupero.
 *
 * I codici escono **una volta sola**, qui: sono l'equivalente di dieci password,
 * e conservarli in chiaro per poterli rimostrare significherebbe tenere dieci
 * password in chiaro nel database. Vanno quindi mostrati subito e con
 * insistenza.
 */
export function confirmMfa(code: string): Promise<MfaConfirmResult> {
  return api.post<MfaConfirmResult>('/me/mfa/confirm', { code }, { organization: false })
}

/**
 * `DELETE /me/mfa` — `204`, con la password come conferma.
 *
 * La password serve perché disattivare il secondo fattore è l'operazione che chi
 * ha rubato una sessione vorrebbe fare per prima. Il corpo su una `DELETE` è
 * insolito ma è ciò che la rotta dichiara.
 */
export function disableMfa(password: string): Promise<void> {
  return request<void>('/me/mfa', {
    method: 'DELETE',
    body: { password },
    organization: false,
  })
}
